// Cognito auth for CheckList (PLAN.md Task 1.1, D-4).
// Email+password: SRP via amazon-cognito-identity-js (no Hosted UI redirect).
// Google: Cognito /oauth2/authorize?identity_provider=Google + manual PKCE;
// Cognito hosted UI is never shown — user goes straight to Google.

import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID as string | undefined;
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID as string | undefined;
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
const GOOGLE_ENABLED_RAW = (import.meta.env.VITE_GOOGLE_ENABLED ?? "false").toLowerCase();

const SK_ACCESS = "checklist.accessToken";
const SK_ID = "checklist.idToken";
const SK_REFRESH = "checklist.refreshToken";
const SK_EXPIRES_AT = "checklist.expiresAt";
const SK_PKCE = "checklist.pkceVerifier";

export function isCognitoConfigured(): boolean {
  return Boolean(USER_POOL_ID && CLIENT_ID);
}

export function isGoogleEnabled(): boolean {
  return GOOGLE_ENABLED_RAW === "true" && Boolean(COGNITO_DOMAIN && CLIENT_ID);
}

let pool: CognitoUserPool | null = null;
function getPool(): CognitoUserPool {
  if (!pool) {
    if (!USER_POOL_ID || !CLIENT_ID) throw new Error("Cognito env vars not set");
    pool = new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID });
  }
  return pool;
}

/** Decode JWT payload without verifying (display / client routing only). */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1] ?? "";
    const padded = payload + "===".slice(0, (4 - (payload.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function storeSession(session: CognitoUserSession): void {
  const expiration = session.getAccessToken().getExpiration();
  const nowSec = Math.floor(Date.now() / 1000);
  localStorage.setItem(SK_ACCESS, session.getAccessToken().getJwtToken());
  localStorage.setItem(SK_ID, session.getIdToken().getJwtToken());
  localStorage.setItem(SK_REFRESH, session.getRefreshToken().getToken());
  localStorage.setItem(
    SK_EXPIRES_AT,
    String(Date.now() + Math.max(0, expiration - nowSec) * 1000)
  );
}

function storeTokenResponse(tokens: {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}): void {
  localStorage.setItem(SK_ACCESS, tokens.access_token);
  localStorage.setItem(SK_ID, tokens.id_token);
  if (tokens.refresh_token) localStorage.setItem(SK_REFRESH, tokens.refresh_token);
  localStorage.setItem(SK_EXPIRES_AT, String(Date.now() + tokens.expires_in * 1000));
}

export function getAccessToken(): string | null {
  return localStorage.getItem(SK_ACCESS);
}

export function getIdToken(): string | null {
  return localStorage.getItem(SK_ID);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(SK_REFRESH);
}

export function isSignedIn(): boolean {
  const access = localStorage.getItem(SK_ACCESS);
  const expires = Number(localStorage.getItem(SK_EXPIRES_AT));
  return Boolean(access && expires && Date.now() < expires);
}

export function getUserEmail(): string {
  const id = getIdToken();
  if (!id) return "";
  const payload = decodeJwtPayload(id);
  return (payload.email as string | undefined) ?? "";
}

export function getUserSub(): string {
  const access = getAccessToken();
  if (!access) return "";
  const payload = decodeJwtPayload(access);
  return (payload.sub as string | undefined) ?? "";
}

export function getGroups(): string[] {
  // Prefer ID token (groups always present); fall back to access token.
  for (const token of [getIdToken(), getAccessToken()]) {
    if (!token) continue;
    const groups = decodeJwtPayload(token)["cognito:groups"];
    if (Array.isArray(groups)) return groups as string[];
  }
  return [];
}

export function signOut(): void {
  [SK_ACCESS, SK_ID, SK_REFRESH, SK_EXPIRES_AT, SK_PKCE].forEach((k) =>
    localStorage.removeItem(k)
  );
}

export function cognitoErrorCode(err: unknown): string {
  if (err && typeof err === "object" && "code" in err && typeof (err as { code: unknown }).code === "string") {
    return (err as { code: string }).code;
  }
  if (err && typeof err === "object" && "name" in err && typeof (err as { name: unknown }).name === "string") {
    return (err as { name: string }).name;
  }
  return "";
}

export function cognitoErrorMessage(err: unknown, fallback = "Something went wrong. Please try again."): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Deterministic, non-email username derived from the normalized email.
 *
 * The pool uses AliasAttributes (signInAliases: {email, username} — see
 * STANDARD.md section 1), so the email alias only becomes active once it's
 * verified. SignUp, ConfirmSignUp, and ResendConfirmationCode all run before
 * (or as) that verification happens, so none of them accept the email alias
 * as Username — each needs the account's real username. Deriving it from the
 * email (rather than a random value persisted across the register→verify
 * page transition) means it can be recomputed from just the email at any
 * point, surviving a page refresh, a closed tab, or a fresh /verify visit.
 * Sign-in, ForgotPassword, and ConfirmForgotPassword run only after
 * confirmation, once the alias is active, so they use email directly.
 */
async function deriveSignupUsername(email: string): Promise<string> {
  const normalized = email.trim().toLowerCase();
  return `u${await sha256Base64Url(normalized)}`;
}

/**
 * Register with email + password. Cognito emails a verification code.
 *
 * `captchaToken` is a Cloudflare Turnstile token; it rides along as SignUp
 * ClientMetadata and the PreSignUp trigger verifies it server-side to block
 * bot sign-ups (auth STANDARD.md §2).
 */
export async function signUp(
  email: string,
  password: string,
  captchaToken?: string
): Promise<{ userConfirmed: boolean }> {
  const username = await deriveSignupUsername(email);
  const attributes = [new CognitoUserAttribute({ Name: "email", Value: email })];
  const clientMetadata = captchaToken ? { turnstileToken: captchaToken } : undefined;
  return new Promise((resolve, reject) => {
    getPool().signUp(
      username,
      password,
      attributes,
      [],
      (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve({ userConfirmed: result?.userConfirmed ?? false });
      },
      clientMetadata
    );
  });
}

/** Confirm signup with the email verification code. */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  const username = await deriveSignupUsername(email);
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: getPool() });
    user.confirmRegistration(code, true, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

/** Resend the signup verification code. */
export async function resendConfirmationCode(email: string): Promise<void> {
  const username = await deriveSignupUsername(email);
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: getPool() });
    user.resendConfirmationCode((err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

/** SRP sign-in. Stores tokens on success. */
export function signIn(email: string, password: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    const auth = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(auth, {
      onSuccess: (session) => {
        storeSession(session);
        resolve();
      },
      onFailure: (err) => reject(err),
    });
  });
}

/** Start forgot-password: Cognito emails a reset code. */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
}

/** Complete forgot-password with code + new password. */
export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Refresh access/id tokens using the stored refresh token.
 * Returns true on success (tokens rewritten to localStorage).
 */
export async function refreshSession(): Promise<boolean> {
  if (!isCognitoConfigured()) return false;
  const storedRefresh = localStorage.getItem(SK_REFRESH);
  const storedId = localStorage.getItem(SK_ID);
  if (!storedRefresh || !storedId) return false;

  const payload = decodeJwtPayload(storedId);
  const username = (payload.email ?? payload["cognito:username"] ?? payload.sub) as string | undefined;
  if (!username) return false;

  return new Promise((resolve) => {
    const user = new CognitoUser({ Username: username, Pool: getPool() });
    const token = new CognitoRefreshToken({ RefreshToken: storedRefresh });
    user.refreshSession(token, (err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(false);
        return;
      }
      storeSession(session);
      resolve(true);
    });
  });
}

// ---- Google OAuth + PKCE (D-4) ----

function base64Url(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let str = "";
  for (const b of arr) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64Url(hash);
}

function oauthRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/** Build Google authorize URL and stash PKCE verifier; caller should navigate to it. */
export async function beginGoogleSignIn(): Promise<string> {
  if (!isGoogleEnabled() || !COGNITO_DOMAIN || !CLIENT_ID) {
    throw new Error("Google sign-in is not enabled");
  }
  const verifier = randomVerifier();
  sessionStorage.setItem(SK_PKCE, verifier);
  const challenge = await sha256Base64Url(verifier);
  const params = new URLSearchParams({
    identity_provider: "Google",
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: oauthRedirectUri(),
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: challenge,
  });
  return `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

/** Exchange authorization code for tokens (callback page). */
export async function completeOAuthCode(code: string): Promise<void> {
  if (!COGNITO_DOMAIN || !CLIENT_ID) throw new Error("Cognito OAuth not configured");
  const verifier = sessionStorage.getItem(SK_PKCE);
  sessionStorage.removeItem(SK_PKCE);
  if (!verifier) throw new Error("Missing PKCE verifier — start Google sign-in again");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    redirect_uri: oauthRedirectUri(),
    code_verifier: verifier,
  });

  const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const msg = (data.error_description as string) ?? (data.error as string) ?? `Token exchange failed (${res.status})`;
    throw new Error(msg);
  }
  if (
    typeof data.access_token !== "string" ||
    typeof data.id_token !== "string" ||
    typeof data.expires_in !== "number"
  ) {
    throw new Error("Token response missing required fields");
  }
  storeTokenResponse({
    access_token: data.access_token,
    id_token: data.id_token,
    refresh_token: typeof data.refresh_token === "string" ? data.refresh_token : undefined,
    expires_in: data.expires_in,
  });
}
